import { API_URL } from '@/config';
import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { Home, Users, Calendar, Image as ImageIcon, User, Crown, LogOut, X, ChevronRight, Settings, Bell, Phone, CreditCard, Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${API_URL}/api`;
const LIONS_LOGO = 'https://customer-assets.emergentagent.com/job_jaipur-lions/artifacts/ex49q6vl_district_logo_lionsclub-removebg.png';
const KANYA_VIVAH_URL = process.env.REACT_APP_KANYA_VIVAH_URL || 'https://kanyavivah.lionsclubhawamahal.com';

const MemberLayout = ({ user }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const isAdmin = user && ['Super Admin', 'President', 'Secretary', 'Treasurer'].includes(user.role);

  const navItems = [
    { path: '/app', label: 'Home', icon: Home },
    { path: '/app/members', label: 'Members', icon: Users },
    { path: '/app/events', label: 'Events', icon: Calendar },
    { path: '/app/gallery', label: 'Gallery', icon: ImageIcon },
    { path: '/app/profile', label: 'Profile', icon: User },
  ];

  const drawerMenuItems = [
    { path: '/app', label: 'Home', icon: Home },
    { path: '/app/members', label: 'Members Directory', icon: Users },
    { path: '/app/events', label: 'Events', icon: Calendar },
    { path: '/app/gallery', label: 'Photo Gallery', icon: ImageIcon },
    { path: '/app/payments', label: 'Payments', icon: CreditCard },
    { path: '/app/profile', label: 'My Profile', icon: User },
  ];

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` } });
      // Clear stored auth data
      localStorage.removeItem('session_token');
      localStorage.removeItem('user');
      toast.success('Logged out successfully');
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Logout error:', error);
      // Clear stored auth data even on error
      localStorage.removeItem('session_token');
      localStorage.removeItem('user');
      navigate('/', { replace: true });
    }
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#0F172A' }}>
      {/* Mobile Header */}
      <header className="md:hidden sticky top-0 z-50 px-4 py-3" style={{ backgroundColor: '#0F172A' }}>
        <div className="flex items-center justify-between">
          {/* Drawer Menu Button */}
          <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
            <SheetTrigger asChild>
              <button className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(212, 175, 55, 0.1)' }}>
                <div className="w-6 h-5 flex flex-col justify-between">
                  <span className="block h-0.5 w-full rounded" style={{ backgroundColor: '#D4AF37' }}></span>
                  <span className="block h-0.5 w-full rounded" style={{ backgroundColor: '#D4AF37' }}></span>
                  <span className="block h-0.5 w-full rounded" style={{ backgroundColor: '#D4AF37' }}></span>
                </div>
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="w-80 p-0" style={{ backgroundColor: '#0F172A', borderColor: '#1E293B' }}>
              <SheetHeader className="p-6 border-b" style={{ borderColor: '#1E293B' }}>
                <div className="flex items-center gap-4">
                  <Avatar className="w-16 h-16 border-2" style={{ borderColor: '#D4AF37' }}>
                    <AvatarImage src={user?.photo_url} />
                    <AvatarFallback style={{ backgroundColor: '#1E293B', color: '#D4AF37' }}>
                      {getInitials(user?.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <SheetTitle className="text-left" style={{ color: '#FFFFFF' }}>{user?.name || 'Member'}</SheetTitle>
                    <p className="text-sm" style={{ color: '#D4AF37' }}>{user?.role || 'Member'}</p>
                  </div>
                </div>
              </SheetHeader>
              
              <nav className="p-4 space-y-2">
                {drawerMenuItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setDrawerOpen(false)}
                      className="flex items-center justify-between px-4 py-3 rounded-xl transition-all"
                      style={{
                        backgroundColor: isActive ? 'rgba(212, 175, 55, 0.15)' : 'rgba(255,255,255,0.05)',
                        color: isActive ? '#D4AF37' : '#E2E8F0'
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <Icon className="w-5 h-5" />
                        <span className="font-medium">{item.label}</span>
                      </div>
                      <ChevronRight className="w-4 h-4" />
                    </Link>
                  );
                })}
                
                {isAdmin && (
                  <Link
                    to="/admin"
                    onClick={() => setDrawerOpen(false)}
                    className="flex items-center justify-between px-4 py-3 rounded-xl mt-4"
                    style={{ backgroundColor: '#D4AF37', color: '#0F172A' }}
                  >
                    <div className="flex items-center gap-3">
                      <Crown className="w-5 h-5" />
                      <span className="font-medium">Admin Panel</span>
                    </div>
                    <ChevronRight className="w-4 h-4" />
                  </Link>
                )}

                <a href={KANYA_VIVAH_URL} target="_blank" rel="noopener noreferrer" onClick={() => setDrawerOpen(false)} className="flex items-center justify-between px-4 py-3 rounded-xl mt-2" style={{ backgroundColor: '#8B1E2B', color: '#FFFFFF' }} data-testid="member-drawer-kanya-vivah-link">
                  <div className="flex items-center gap-3"><Heart className="w-5 h-5" fill="currentColor" /><span className="font-medium">101 Kanya Vivah</span></div>
                  <ChevronRight className="w-4 h-4" />
                </a>
              </nav>
              
              <div className="absolute bottom-0 left-0 right-0 p-4 border-t" style={{ borderColor: '#1E293B' }}>
                <button
                  onClick={() => {
                    setDrawerOpen(false);
                    handleLogout();
                  }}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl w-full"
                  style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)', color: '#EF4444' }}
                >
                  <LogOut className="w-5 h-5" />
                  <span className="font-medium">Logout</span>
                </button>
              </div>
            </SheetContent>
          </Sheet>
          
          {/* Center - Logo & Club Name */}
          <div className="flex items-center gap-2">
            <img src={LIONS_LOGO} alt="Lions Club" className="w-10 h-10 object-contain" />
            <div className="text-center">
              <h1 className="text-sm font-bold" style={{ color: '#FFFFFF' }}>Lions Club</h1>
              <p className="text-xs" style={{ color: '#D4AF37' }}>Jaipur Hawamahal</p>
            </div>
          </div>
          
          {/* Right - Profile Photo */}
          <Link to="/app/profile">
            <Avatar className="w-10 h-10 border-2" style={{ borderColor: '#D4AF37' }}>
              <AvatarImage src={user?.photo_url} />
              <AvatarFallback style={{ backgroundColor: '#1E293B', color: '#D4AF37' }}>
                {getInitials(user?.name)}
              </AvatarFallback>
            </Avatar>
          </Link>
        </div>
      </header>
      
      {/* Desktop Header */}
      <header className="hidden md:block border-b bg-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <img src={LIONS_LOGO} alt="Lions Club" className="w-12 h-12 object-contain" />
              <div>
                <h1 className="text-lg font-bold" style={{ color: '#0F172A' }}>Lions Club</h1>
                <p className="text-xs" style={{ color: '#D4AF37' }}>Jaipur Hawamahal</p>
              </div>
            </div>
            
            <div className="flex items-center gap-6">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all ${
                      isActive ? 'font-medium' : 'text-muted-foreground hover:text-foreground'
                    }`}
                    style={isActive ? { color: '#D4AF37' } : {}}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
              
              {isAdmin && (
                <Link
                  to="/admin"
                  className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all"
                  style={{ backgroundColor: '#0F172A', color: '#F8FAFC' }}
                >
                  <Crown className="w-4 h-4" />
                  <span>Admin</span>
                </Link>
              )}
              
              <a href={KANYA_VIVAH_URL} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all hover:-translate-y-0.5" style={{ backgroundColor: '#8B1E2B', color: '#FFFFFF' }} data-testid="member-kanya-vivah-link">
                <Heart className="w-4 h-4" fill="currentColor" /><span>101 Kanya Vivah</span>
              </a>
              <Button variant="ghost" size="icon" onClick={handleLogout}>
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>
      
      {/* Main Content - single Outlet with responsive wrapper (dark/full-bleed on mobile, contained light on desktop) */}
      <main className="flex-1 md:bg-gray-50">
        <div className="pb-24 md:pb-8 md:max-w-7xl md:mx-auto md:px-6 lg:px-8 md:py-8">
          <Outlet context={{ user }} />
        </div>
      </main>
      
      {/* Mobile Bottom Navigation */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50" style={{ backgroundColor: '#1E293B' }}>
        <div className="flex items-center justify-around py-2 px-2 relative">
          {/* Members */}
          <Link to="/app/members" className="flex flex-col items-center py-2 flex-1">
            <Users className="w-5 h-5 mb-1" style={{ color: location.pathname === '/app/members' ? '#D4AF37' : '#64748B' }} />
            <span className="text-xs" style={{ color: location.pathname === '/app/members' ? '#D4AF37' : '#64748B' }}>Members</span>
          </Link>
          
          {/* Events */}
          <Link to="/app/events" className="flex flex-col items-center py-2 flex-1">
            <Calendar className="w-5 h-5 mb-1" style={{ color: location.pathname === '/app/events' ? '#D4AF37' : '#64748B' }} />
            <span className="text-xs" style={{ color: location.pathname === '/app/events' ? '#D4AF37' : '#64748B' }}>Events</span>
          </Link>
          
          {/* Home - Center Elevated */}
          <Link to="/app" className="flex flex-col items-center relative -mt-8 flex-1">
            <div
              className="w-14 h-14 rounded-full flex items-center justify-center shadow-lg"
              style={{
                backgroundColor: location.pathname === '/app' ? '#D4AF37' : '#0F172A',
                border: '3px solid #1E293B'
              }}
            >
              <Home className="w-6 h-6" style={{ color: location.pathname === '/app' ? '#0F172A' : '#D4AF37' }} />
            </div>
            <span className="text-xs mt-1" style={{ color: location.pathname === '/app' ? '#D4AF37' : '#64748B' }}>Home</span>
          </Link>
          
          {/* Gallery */}
          <Link to="/app/gallery" className="flex flex-col items-center py-2 flex-1">
            <ImageIcon className="w-5 h-5 mb-1" style={{ color: location.pathname === '/app/gallery' ? '#D4AF37' : '#64748B' }} />
            <span className="text-xs" style={{ color: location.pathname === '/app/gallery' ? '#D4AF37' : '#64748B' }}>Gallery</span>
          </Link>
          
          {/* Profile */}
          <Link to="/app/profile" className="flex flex-col items-center py-2 flex-1">
            <User className="w-5 h-5 mb-1" style={{ color: location.pathname === '/app/profile' ? '#D4AF37' : '#64748B' }} />
            <span className="text-xs" style={{ color: location.pathname === '/app/profile' ? '#D4AF37' : '#64748B' }}>Profile</span>
          </Link>
        </div>
      </nav>
    </div>
  );
};

export default MemberLayout;
